import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Save, X, Quote, Star, StarOff } from "lucide-react";
import { api } from "@/lib/api";
import RichEditor from "@/components/RichEditor";

const EMPTY_AUTHOR = { name: "", role: "", photo_url: "", bio: "", twitter: "", instagram: "", active: true };
const EMPTY_ARTICLE = { title: "", lead: "", body: "", opinion_author_id: "", image: "", status: "published", featured: false };

export default function AdminOpinionTab() {
  const [section, setSection] = useState("articles");
  const [authors, setAuthors] = useState([]);
  const [articles, setArticles] = useState([]);
  const [authorDraft, setAuthorDraft] = useState(null);
  const [articleDraft, setArticleDraft] = useState(null);

  const reload = async () => {
    try {
      const [a, art] = await Promise.all([
        api.get("/admin/opinion/authors"),
        api.get("/admin/opinion/articles"),
      ]);
      setAuthors(a.data || []);
      setArticles(art.data || []);
    } catch { toast.error("Error cargando opiniones"); }
  };

  useEffect(() => { reload(); }, []);

  // ----- Authors -----
  const saveAuthor = async () => {
    try {
      if (authorDraft.id) {
        await api.put(`/admin/opinion/authors/${authorDraft.id}`, authorDraft);
      } else {
        await api.post("/admin/opinion/authors", authorDraft);
      }
      toast.success("Columnista guardado");
      setAuthorDraft(null);
      await reload();
    } catch { toast.error("Error guardando"); }
  };
  const deleteAuthor = async (id) => {
    if (!window.confirm("¿Eliminar el columnista y todas sus columnas?")) return;
    try {
      await api.delete(`/admin/opinion/authors/${id}`);
      toast.success("Columnista eliminado");
      await reload();
    } catch { toast.error("Error eliminando"); }
  };

  // ----- Articles -----
  const saveArticle = async () => {
    if (!articleDraft.opinion_author_id) {
      toast.error("Seleccioná un columnista");
      return;
    }
    try {
      if (articleDraft.id) {
        await api.put(`/admin/opinion/articles/${articleDraft.id}`, articleDraft);
      } else {
        await api.post("/admin/opinion/articles", articleDraft);
      }
      toast.success("Columna guardada");
      setArticleDraft(null);
      await reload();
    } catch { toast.error("Error guardando"); }
  };
  const deleteArticle = async (id) => {
    if (!window.confirm("¿Eliminar la columna?")) return;
    try {
      await api.delete(`/admin/opinion/articles/${id}`);
      toast.success("Eliminada");
      await reload();
    } catch { toast.error("Error eliminando"); }
  };
  const toggleFeatured = async (art) => {
    try {
      await api.put(`/admin/opinion/articles/${art.id}`, { ...art, featured: !art.featured });
      await reload();
    } catch { toast.error("Error"); }
  };

  const authorById = (id) => authors.find((a) => a.id === id);

  return (
    <div data-testid="admin-opinion-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-headline uppercase text-3xl md:text-4xl flex items-center gap-2"><Quote size={26} /> Opinión</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            Gestioná columnistas y columnas de opinión
          </p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 mb-6 border-b" style={{ borderColor: "var(--border)" }}>
        {[
          { k: "articles", label: `Columnas (${articles.length})` },
          { k: "authors", label: `Columnistas (${authors.length})` },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setSection(t.k)}
            className="px-4 py-3 text-xs uppercase tracking-widest font-bold"
            style={{ color: section === t.k ? "#ec4899" : "var(--text-muted)", borderBottom: section === t.k ? "2px solid #ec4899" : "2px solid transparent" }}
            data-testid={`admin-opinion-subtab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ARTICLES */}
      {section === "articles" && (
        <div>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setArticleDraft({ ...EMPTY_ARTICLE, opinion_author_id: authors[0]?.id || "" })}
              className="btn-accent"
              data-testid="admin-opinion-new-article"
            >
              <Plus size={16} /> Nueva columna
            </button>
          </div>
          {authors.length === 0 ? (
            <div className="text-center py-12 border rounded" style={{ borderColor: "var(--border)" }}>
              <p className="text-sm mb-3">No hay columnistas creados aún.</p>
              <button onClick={() => setSection("authors")} className="text-xs font-bold uppercase" style={{ color: "#ec4899" }}>
                → Crear el primer columnista
              </button>
            </div>
          ) : articles.length === 0 ? (
            <p className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>Sin columnas todavía.</p>
          ) : (
            <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--border)" }}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-left" style={{ background: "var(--surface-2)" }}>
                    <th className="p-3">Título</th>
                    <th className="p-3">Columnista</th>
                    <th className="p-3">Estado</th>
                    <th className="p-3">Destacado</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((a) => {
                    const auth = authorById(a.opinion_author_id);
                    return (
                      <tr key={a.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`admin-opinion-row-${a.id}`}>
                        <td className="p-3 font-bold">{a.title}</td>
                        <td className="p-3">{auth?.name || "—"}</td>
                        <td className="p-3 uppercase text-xs">{a.status}</td>
                        <td className="p-3">
                          <button onClick={() => toggleFeatured(a)} title="Toggle destacado" data-testid={`admin-opinion-feature-${a.id}`}>
                            {a.featured ? <Star size={16} fill="#ec4899" color="#ec4899" /> : <StarOff size={16} className="opacity-40" />}
                          </button>
                        </td>
                        <td className="p-3 text-right">
                          <button onClick={() => setArticleDraft(a)} className="mr-2" data-testid={`admin-opinion-edit-${a.id}`}><Edit size={14} /></button>
                          <button onClick={() => deleteArticle(a.id)} data-testid={`admin-opinion-delete-${a.id}`}><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* AUTHORS */}
      {section === "authors" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setAuthorDraft({ ...EMPTY_AUTHOR })} className="btn-accent" data-testid="admin-opinion-new-author">
              <Plus size={16} /> Nuevo columnista
            </button>
          </div>
          {authors.length === 0 ? (
            <p className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>Sin columnistas todavía.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {authors.map((a) => (
                <div key={a.id} className="border rounded-lg p-4 flex items-start gap-3" style={{ borderColor: "var(--border)" }} data-testid={`admin-opinion-author-${a.id}`}>
                  {a.photo_url ? (
                    <img src={a.photo_url} alt={a.name} className="w-14 h-14 rounded-full object-cover" style={{ border: "2px solid #ec4899" }} />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gray-200" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{a.name}</p>
                    {a.role && <p className="text-xs italic" style={{ color: "#ec4899" }}>{a.role}</p>}
                    {a.bio && <p className="text-xs mt-1 line-clamp-2" style={{ color: "var(--text-muted)" }}>{a.bio}</p>}
                    <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{a.active ? "✅ Activo" : "⏸ Inactivo"}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setAuthorDraft(a)} data-testid={`admin-opinion-edit-author-${a.id}`}><Edit size={14} /></button>
                    <button onClick={() => deleteAuthor(a.id)} data-testid={`admin-opinion-delete-author-${a.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* AUTHOR MODAL */}
      {authorDraft && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setAuthorDraft(null)}>
          <div className="bg-white rounded-lg p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto" style={{ background: "var(--surface)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-headline uppercase text-xl">{authorDraft.id ? "Editar" : "Nuevo"} columnista</h2>
              <button onClick={() => setAuthorDraft(null)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input type="text" placeholder="Nombre completo" value={authorDraft.name} onChange={(e) => setAuthorDraft({ ...authorDraft, name: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-opinion-author-name" />
              <input type="text" placeholder="Cargo / Rol (Ej: Columnista política)" value={authorDraft.role || ""} onChange={(e) => setAuthorDraft({ ...authorDraft, role: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-opinion-author-role" />
              <input type="text" placeholder="URL de la foto" value={authorDraft.photo_url || ""} onChange={(e) => setAuthorDraft({ ...authorDraft, photo_url: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-opinion-author-photo" />
              <textarea placeholder="Bio breve" value={authorDraft.bio || ""} onChange={(e) => setAuthorDraft({ ...authorDraft, bio: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-transparent" rows={3} style={{ borderColor: "var(--border)" }} />
              <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="@twitter" value={authorDraft.twitter || ""} onChange={(e) => setAuthorDraft({ ...authorDraft, twitter: e.target.value })} className="px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
                <input type="text" placeholder="@instagram" value={authorDraft.instagram || ""} onChange={(e) => setAuthorDraft({ ...authorDraft, instagram: e.target.value })} className="px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={authorDraft.active !== false} onChange={(e) => setAuthorDraft({ ...authorDraft, active: e.target.checked })} />
                Activo (visible en el sitio)
              </label>
              <button onClick={saveAuthor} className="btn-accent w-full" data-testid="admin-opinion-author-save">
                <Save size={14} /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ARTICLE MODAL */}
      {articleDraft && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setArticleDraft(null)}>
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[92vh] overflow-y-auto" style={{ background: "var(--surface)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-headline uppercase text-xl">{articleDraft.id ? "Editar" : "Nueva"} columna</h2>
              <button onClick={() => setArticleDraft(null)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input type="text" placeholder="Título" value={articleDraft.title} onChange={(e) => setArticleDraft({ ...articleDraft, title: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-opinion-article-title" />
              <textarea placeholder="Lead / bajada (1-2 líneas)" value={articleDraft.lead || ""} onChange={(e) => setArticleDraft({ ...articleDraft, lead: e.target.value })} rows={2} className="w-full px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
              <select value={articleDraft.opinion_author_id} onChange={(e) => setArticleDraft({ ...articleDraft, opinion_author_id: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-opinion-article-author">
                <option value="">— Seleccionar columnista —</option>
                {authors.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.role || "—"})</option>)}
              </select>
              <input type="text" placeholder="URL imagen de portada (opcional)" value={articleDraft.image || ""} onChange={(e) => setArticleDraft({ ...articleDraft, image: e.target.value })} className="w-full px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
              <RichEditor value={articleDraft.body} onChange={(html) => setArticleDraft({ ...articleDraft, body: html })} />
              <div className="grid grid-cols-2 gap-2">
                <select value={articleDraft.status} onChange={(e) => setArticleDraft({ ...articleDraft, status: e.target.value })} className="px-3 py-2 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }}>
                  <option value="published">Publicado</option>
                  <option value="draft">Borrador</option>
                </select>
                <label className="flex items-center gap-2 text-sm px-3 py-2 border rounded" style={{ borderColor: "var(--border)" }}>
                  <input type="checkbox" checked={!!articleDraft.featured} onChange={(e) => setArticleDraft({ ...articleDraft, featured: e.target.checked })} />
                  Destacar (Card grande)
                </label>
              </div>
              <button onClick={saveArticle} className="btn-accent w-full" data-testid="admin-opinion-article-save">
                <Save size={14} /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
